import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { parseTags, getAllTags, getTagCounts, getMajorCategoryCounts, designMatchesTag, getCategoryColor } from '../utils/tagUtils';
import { fetchCategories } from '../utils/categories';
import { API_BASE_URL } from '../config/api';

function CategoriesAndTagsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchMode, setSearchMode] = useState('within'); // 'within' or 'outside'
  const [categoriesData, setCategoriesData] = useState(null);
  const [majorCategories, setMajorCategories] = useState([]);

  // Session storage key for saving filters
  const FILTERS_STORAGE_KEY = 'categoriesTagsFilters';

  useEffect(() => {
    document.title = 'Search - Design Store';
    fetchDesigns();
    fetchCategories().then(categories => {
      setCategoriesData(categories);
      setMajorCategories(categories.categories.map(c => c.name));
    });

    // Priority: location.state (from navigation) > sessionStorage > URL params
    const filtersFromState = location.state?.filters;
    const filtersFromStorage = (() => {
      try {
        const stored = sessionStorage.getItem(FILTERS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    })();

    if (filtersFromState) {
      // Restore from navigation state
      setSelectedCategory(filtersFromState.category || null);
      setSelectedTag(filtersFromState.tag || null);
      setSearchQuery(filtersFromState.searchQuery || '');
      setSearchMode(filtersFromState.searchMode || 'within');
      
      // Update URL params
      const newParams = new URLSearchParams();
      if (filtersFromState.category) {
        newParams.set('category', filtersFromState.category);
      }
      if (filtersFromState.tag) {
        newParams.set('tag', filtersFromState.tag);
      }
      setSearchParams(newParams, { replace: true });
      
      // Clear sessionStorage after restoring
      sessionStorage.removeItem(FILTERS_STORAGE_KEY);
    } else if (filtersFromStorage) {
      // Restore from sessionStorage (fallback)
      setSelectedCategory(filtersFromStorage.category || null);
      setSelectedTag(filtersFromStorage.tag || null);
      setSearchQuery(filtersFromStorage.searchQuery || '');
      setSearchMode(filtersFromStorage.searchMode || 'within');
      
      // Update URL params
      const newParams = new URLSearchParams();
      if (filtersFromStorage.category) {
        newParams.set('category', filtersFromStorage.category);
      }
      if (filtersFromStorage.tag) {
        newParams.set('tag', filtersFromStorage.tag);
      }
      setSearchParams(newParams, { replace: true });
      
      // Clear sessionStorage after restoring
      sessionStorage.removeItem(FILTERS_STORAGE_KEY);
    } else {
      // Read filters from URL (default behavior)
      const categoryFromUrl = searchParams.get('category');
      const tagFromUrl = searchParams.get('tag');
      if (categoryFromUrl) {
        setSelectedCategory(decodeURIComponent(categoryFromUrl));
      }
      if (tagFromUrl) {
        setSelectedTag(decodeURIComponent(tagFromUrl));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- init from URL on mount
  }, []);

  const fetchDesigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/designs`);
      if (response.data.success) {
        setDesigns(response.data.data);
      } else {
        setError('Failed to load designs');
      }
    } catch (err) {
      console.error('Error fetching designs:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load designs');
    } finally {
      setLoading(false);
    }
  };

  // Get all unique tags (excluding major categories)
  const allTags = useMemo(() => {
    const tags = new Set();
    const majorCategoriesLower = majorCategories.map(c => c.toLowerCase());
    designs.forEach(design => {
      if (design.tags) {
        const { minorTags } = parseTags(design.tags);
        minorTags.forEach(tag => {
          if (tag && !majorCategoriesLower.includes(tag.toLowerCase())) {
            tags.add(tag);
          }
        });
      }
    });
    return Array.from(tags).sort();
  }, [designs, majorCategories]);

  // Get tag counts
  const tagCounts = useMemo(() => getTagCounts(designs), [designs]);
  
  // Get category counts
  const categoryCounts = useMemo(() => 
    getMajorCategoryCounts(designs, majorCategories), 
    [designs, majorCategories]
  );

  // Get tags that belong to the selected category
  const visibleTags = useMemo(() => {
    if (!selectedCategory) {
      // If no category selected, show all tags
      return allTags;
    }
    
    // Get all designs in the selected category
    let categoryDesigns = designs.filter(design => {
      if (!design.tags) return false;
      const { majorCategory } = parseTags(design.tags);
      return majorCategory && majorCategory.toLowerCase() === selectedCategory.toLowerCase();
    });
    
    // If a tag is also selected, further filter by that tag
    if (selectedTag) {
      categoryDesigns = categoryDesigns.filter(design => designMatchesTag(design, selectedTag));
    }
    
    // Extract unique tags from these designs
    const tags = new Set();
    const majorCategoriesLower = majorCategories.map(c => c.toLowerCase());
    categoryDesigns.forEach(design => {
      if (design.tags) {
        const { minorTags } = parseTags(design.tags);
        minorTags.forEach(tag => {
          if (tag && !majorCategoriesLower.includes(tag.toLowerCase())) {
            tags.add(tag);
          }
        });
      }
    });
    return Array.from(tags).sort();
  }, [selectedCategory, selectedTag, designs, allTags, majorCategories]);

  // Get categories that have designs with the selected tag
  const visibleCategories = useMemo(() => {
    if (!selectedTag) {
      // If no tag selected, show all categories
      return categoriesData ? categoriesData.categories : [];
    }
    
    // Get all designs with the selected tag
    let tagDesigns = designs.filter(design => designMatchesTag(design, selectedTag));
    
    // If a category is also selected, further filter by that category
    if (selectedCategory) {
      tagDesigns = tagDesigns.filter(design => {
        if (!design.tags) return false;
        const { majorCategory } = parseTags(design.tags);
        return majorCategory && majorCategory.toLowerCase() === selectedCategory.toLowerCase();
      });
    }
    
    // Extract unique categories from these designs
    const categories = new Set();
    tagDesigns.forEach(design => {
      if (design.tags) {
        const { majorCategory } = parseTags(design.tags);
        if (majorCategory) {
          categories.add(majorCategory);
        }
      }
    });
    
    // Return categories in the same order as categoriesData, filtered to only those that exist
    if (!categoriesData) return [];
    return categoriesData.categories.filter(cat => 
      Array.from(categories).some(c => c.toLowerCase() === cat.name.toLowerCase())
    );
  }, [selectedTag, selectedCategory, designs, categoriesData]);

  // Filter designs based on selected category/tag
  const filteredByCategoryTag = useMemo(() => {
    let filtered = designs;

    if (selectedCategory) {
      filtered = filtered.filter(design => {
        if (!design.tags) return false;
        const { majorCategory } = parseTags(design.tags);
        return majorCategory && majorCategory.toLowerCase() === selectedCategory.toLowerCase();
      });
    }

    if (selectedTag) {
      filtered = filtered.filter(design => designMatchesTag(design, selectedTag));
    }

    return filtered;
  }, [designs, selectedCategory, selectedTag]);

  // Apply search based on mode
  const filteredDesigns = useMemo(() => {
    if (!searchQuery.trim()) {
      return filteredByCategoryTag;
    }

    const query = searchQuery.toLowerCase().trim();
    
    // If searching "outside", search all designs regardless of category/tag filter
    // If searching "within", only search in the filtered results
    const searchBase = searchMode === 'within' ? filteredByCategoryTag : designs;

    return searchBase.filter(design => {
      const title = (design.title || '').toLowerCase();
      const description = (design.description || '').toLowerCase();
      const tags = design.tags ? getAllTags(design.tags).join(' ').toLowerCase() : '';
      return title.includes(query) || description.includes(query) || tags.includes(query);
    });
  }, [searchQuery, searchMode, filteredByCategoryTag, designs]);

  const handleCategoryClick = (category) => {
    // If category is empty string or null, clear selection
    const newCategory = category && category !== '' ? category : null;
    setSelectedCategory(newCategory);
    // Don't clear tag - allow both to be selected
    
    const newParams = new URLSearchParams(searchParams);
    if (newCategory) {
      newParams.set('category', newCategory);
    } else {
      newParams.delete('category');
    }
    setSearchParams(newParams, { replace: true });
  };

  const handleTagClick = (tag) => {
    const newTag = selectedTag === tag ? null : tag;
    setSelectedTag(newTag);
    // Don't clear category - allow both to be selected
    
    const newParams = new URLSearchParams(searchParams);
    if (newTag) {
      newParams.set('tag', newTag);
    } else {
      newParams.delete('tag');
    }
    setSearchParams(newParams, { replace: true });
  };

  const handleClearFilters = () => {
    setSelectedCategory(null);
    setSelectedTag(null);
    setSearchQuery('');
    setSearchParams({}, { replace: true });
  };

  const handleDesignClick = (designId) => {
    // Save current filter state to sessionStorage
    const filtersToSave = {
      category: selectedCategory,
      tag: selectedTag,
      searchQuery: searchQuery,
      searchMode: searchMode
    };
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
    } catch (e) {
      console.error('Error saving filters to sessionStorage:', e);
    }
    
    // Navigate with state (include filters for restoration)
    navigate(`/design/${designId}`, { 
      state: { 
        fromCategoriesTags: true,
        filters: filtersToSave // Pass filters for restoration when returning
      } 
    });
  };

  // Format category name to proper case (title case)
  const formatCategoryName = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading categories and tags...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchDesigns}
              className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Search</h1>
          <p className="text-gray-600">Browse all categories and tags, then filter designs</p>
        </header>

        {/* Filters Section - Two columns: Search+Category (35%) | Tags (65%) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* Left Column: Search + Category (35%) */}
            <div className="md:w-[35%] md:min-w-0 flex-shrink-0 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Designs
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by title, description, or tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 pl-10 pr-4 text-base text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory || ''}
                  onChange={(e) => handleCategoryClick(e.target.value || null)}
                  className="w-full px-4 py-3 text-base text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Categories</option>
                  {visibleCategories.map((category) => {
                    const count = categoryCounts[category.name] || 0;
                    return (
                      <option key={category.name} value={category.name}>
                        {category.name} ({count})
                      </option>
                    );
                  })}
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  {selectedTag && !selectedCategory
                    ? `${visibleCategories.length} categories with "${selectedTag}" tag`
                    : selectedCategory && selectedTag
                    ? `${visibleCategories.length} categories (filtered by "${selectedCategory}" and "${selectedTag}")`
                    : `${Object.keys(categoryCounts).length} categories • ${designs.length} total designs`}
                </p>
              </div>

              {/* Search Mode Toggle */}
              {searchQuery.trim() && (selectedCategory || selectedTag) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Mode
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSearchMode('within')}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        searchMode === 'within'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Search Within Filter
                    </button>
                    <button
                      onClick={() => setSearchMode('outside')}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        searchMode === 'outside'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Search All Designs
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {searchMode === 'within'
                      ? 'Searching only in designs matching the selected category/tag filter'
                      : 'Searching in all designs, regardless of category/tag filter'}
                  </p>
                </div>
              )}
            </div>

            {/* Right Column: Tags (65%) */}
            <div className="md:flex-1 md:min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Tags</h2>
              <p className="text-sm text-gray-600 mb-3">
                {selectedCategory && !selectedTag
                  ? `${visibleTags.length} tags in "${selectedCategory}" category`
                  : selectedCategory && selectedTag
                  ? `${visibleTags.length} tags (filtered by "${selectedCategory}" and "${selectedTag}")`
                  : `${allTags.length} unique tags`}
              </p>
              <div className="flex flex-wrap gap-2 max-h-64 md:max-h-80 overflow-y-auto">
                {visibleTags.map((tag) => {
                  // Calculate count for this tag within the filtered context
                  let count = tagCounts[tag] || 0;
                  if (selectedCategory) {
                    // Count only designs in the selected category that have this tag
                    const categoryDesigns = designs.filter(design => {
                      if (!design.tags) return false;
                      const { majorCategory } = parseTags(design.tags);
                      if (majorCategory && majorCategory.toLowerCase() === selectedCategory.toLowerCase()) {
                        const allDesignTags = getAllTags(design.tags);
                        return allDesignTags.some(t => t.toLowerCase() === tag.toLowerCase());
                      }
                      return false;
                    });
                    count = categoryDesigns.length;
                  }
                  const isSelected = selectedTag === tag;
                  return (
                    <button
                      key={tag}
                      onClick={() => handleTagClick(tag)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                        isSelected
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tag} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Active Filters - Full width below columns */}
          {(selectedCategory || selectedTag || searchQuery.trim()) && (
            <div className="flex flex-wrap gap-2 items-center pt-4 mt-4 border-t border-gray-200">
              <span className="text-sm text-gray-600">Active filters:</span>
              {selectedCategory && (
                <button
                  onClick={() => handleCategoryClick(selectedCategory)}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium hover:bg-blue-200"
                >
                  Category: {selectedCategory}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {selectedTag && (
                <button
                  onClick={() => handleTagClick(selectedTag)}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium hover:bg-green-200"
                >
                  Tag: {selectedTag}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {searchQuery.trim() && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium hover:bg-gray-200"
                >
                  Search: "{searchQuery}"
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <button
                onClick={handleClearFilters}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 font-medium underline"
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {/* Filtered Designs Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-gray-900">
              Designs
              {filteredDesigns.length !== designs.length && (
                <span className="ml-2 text-lg font-normal text-gray-600">
                  ({filteredDesigns.length} of {designs.length})
                </span>
              )}
            </h2>
          </div>

          {filteredDesigns.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg mb-2">No designs found</p>
              <button
                onClick={handleClearFilters}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredDesigns.map((design) => {
                const parsedTags = parseTags(design.tags || '');
                const { majorCategory } = parsedTags;
                return (
                  <div
                    key={design.id}
                    onClick={() => handleDesignClick(design.id)}
                    className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow cursor-pointer overflow-hidden border border-gray-200"
                  >
                    <div className="aspect-square bg-gray-100 overflow-hidden relative">
                      <img
                        src={`${API_BASE_URL.replace('/api', '')}/api/designs/${design.id}/thumbnail`}
                        alt={design.title}
                        width="400"
                        height="400"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.src = design.thumbnail_url;
                        }}
                      />
                      {majorCategory && (
                        <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium border ${getCategoryColor(majorCategory)}`}>
                          {formatCategoryName(majorCategory)}
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">
                        {design.title}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CategoriesAndTagsPage;
